<template>
  <div
    v-if="aiStore.pendingConfirmation"
    class="tool-confirmation-prompt"
    data-id-e2e="aiToolConfirmationPrompt"
  >
    <div class="confirmation-label">
      <v-icon size="14" class="confirmation-icon">mdi-robot-outline</v-icon>
      <span class="confirmation-label__text"
        >The agent wants to <strong>{{ aiStore.pendingConfirmation.displayLabel }}</strong></span
      >
    </div>
    <div class="confirmation-actions">
      <!-- Yes split button -->
      <v-btn-group
        divided
        density="compact"
        class="confirmation-btn-group confirmation-btn-group--yes"
      >
        <v-btn
          color="primary"
          variant="flat"
          size="small"
          data-id-e2e="aiConfirmYesOnce"
          @click="aiStore.resolveToolConfirmation('yes-once')"
        >
          Yes
        </v-btn>
        <v-menu location="bottom start" :close-on-content-click="true">
          <template #activator="{ props: menuProps }">
            <v-btn
              v-bind="menuProps"
              color="primary"
              variant="flat"
              size="small"
              class="confirmation-chevron"
              data-id-e2e="aiConfirmYesMenuBtn"
            >
              <v-icon size="14" color="white">mdi-chevron-down</v-icon>
            </v-btn>
          </template>
          <v-list density="compact" class="confirmation-menu-list">
            <v-list-item
              data-id-e2e="aiConfirmYesSessionAction"
              @click="aiStore.resolveToolConfirmation('yes-session-action')"
            >
              <v-list-item-title>Yes (This action, this session)</v-list-item-title>
            </v-list-item>
            <v-list-item
              data-id-e2e="aiConfirmYesSessionAll"
              @click="aiStore.resolveToolConfirmation('yes-session-all')"
            >
              <v-list-item-title>Yes (All actions, this session)</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </v-btn-group>

      <!-- No split button -->
      <v-btn-group divided density="compact" class="confirmation-btn-group">
        <v-btn
          variant="outlined"
          size="small"
          data-id-e2e="aiConfirmNoOnce"
          @click="aiStore.resolveToolConfirmation('no-once')"
        >
          No
        </v-btn>
        <v-menu location="bottom start" :close-on-content-click="true">
          <template #activator="{ props: menuProps }">
            <v-btn
              v-bind="menuProps"
              variant="outlined"
              size="small"
              class="confirmation-chevron"
              data-id-e2e="aiConfirmNoMenuBtn"
            >
              <v-icon size="14">mdi-chevron-down</v-icon>
            </v-btn>
          </template>
          <v-list density="compact" class="confirmation-menu-list">
            <v-list-item
              data-id-e2e="aiConfirmNoSessionAction"
              @click="aiStore.resolveToolConfirmation('no-session-action')"
            >
              <v-list-item-title>No (This action, this session)</v-list-item-title>
            </v-list-item>
            <v-list-item
              data-id-e2e="aiConfirmNoSessionAll"
              @click="aiStore.resolveToolConfirmation('no-session-all')"
            >
              <v-list-item-title>No (All actions, this session)</v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
      </v-btn-group>
    </div>
  </div>
</template>

<script setup>
import { useAiStore } from '@/stores/ai';

/**
 * AiToolConfirmationPrompt
 *
 * Displayed inline in the message list when the AI agent wants to execute a mutating
 * tool call and requires user approval before proceeding.
 *
 * The component renders when `aiStore.pendingConfirmation` is non-null. The user
 * chooses from six options (split into two split-buttons):
 *
 *   Yes (Once)                 — approve this single invocation
 *   Yes (This action, session) — approve this tool type for the whole session
 *   Yes (All actions, session) — approve all mutating tools for the session
 *   No (Just this time)       — decline this invocation; LLM may re-offer
 *   No (This action, session) — permanently decline this tool for the session
 *   No (All actions, session) — permanently decline all tools for the session
 *
 * Known limitation: there is no timeout on the confirmation prompt. If the user
 * navigates away or closes the tab, the pending Promise is resolved with 'cancel'
 * by clearAssistantConversation / cancelCurrentRequest in the store.
 */
const aiStore = useAiStore();
</script>

<style scoped>
.tool-confirmation-prompt {
  margin: 8px 0 4px;
  padding: 10px 12px;
  border-radius: 8px;
  border: 1px solid rgba(var(--v-theme-primary), 0.2);
  background: rgba(var(--v-theme-primary), 0.04);
}

.confirmation-label {
  font-size: 13px;
  color: rgb(var(--v-theme-on-surface));
  margin-bottom: 10px;
  line-height: 1.4;
  display: flex;
  align-items: flex-start;
  gap: 6px;
}

.confirmation-label__text {
  flex: 1;
  min-width: 0;
}

.confirmation-icon {
  margin-top: 1px;
  flex-shrink: 0;
  opacity: 0.7;
}

.confirmation-actions {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
}

.confirmation-btn-group {
  flex-shrink: 0;
}

.confirmation-chevron {
  min-width: 28px !important;
  padding: 0 4px !important;
}

/* The divided border between the Yes label and chevron sits on a primary-blue
   background — override it to a translucent white so it stays visible. */
.confirmation-btn-group--yes :deep(.v-btn-group__divider),
.confirmation-btn-group--yes :deep(.v-divider) {
  border-color: rgba(255, 255, 255, 0.45) !important;
  opacity: 1 !important;
}
/* Also cover the border-based divider variant */
.confirmation-btn-group--yes :deep(.v-btn:not(:last-child)) {
  border-inline-end-color: rgba(255, 255, 255, 0.45) !important;
}

.confirmation-menu-list {
  min-width: 220px;
}
</style>
